import React, {
    Suspense,
    ReactNode,
    useState,
    useEffect,
    useCallback
} from "react";
import { useLocation } from "react-router-dom";
import { RouteStore } from "./RouteStore";
import { useSingleton } from "../injection";
import { observer } from "mobx-react-lite";
import { Spinner } from "../../uiLibrary/spinner/Spinner";
import styled from "@emotion/styled";
import { IRoute } from "./routes";
import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import {
    CurrentRouteProvider,
    IBlockedRoute,
    useUnsavedBlockedRoute
} from "./useUnsavedChanges";

export const Page = observer(() => {
    const location = useLocation();
    const routeStore = useSingleton(RouteStore);
    const loggedInUserStore = useSingleton(LoggedInUserStore);

    const route = routeStore.getRouteByPath(location.pathname);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const params = routeStore.getParamsByPath(location.pathname);

    // each page is given a unique key so that react sees them as separate components
    const [keyRefresh, setKeyRefresh] = useState(0);
    const key = `${routeStore.flattenedRoutes
        .findIndex(x => x === route)
        .toString()}_${keyRefresh.toString()}`;
    const refresh = useCallback(() => {
        setKeyRefresh(x => x + 1);
    }, []);

    // page change effect
    const [previousRoute, setPreviousRoute] = useState<IRoute | undefined>();
    useEffect(() => {
        if (previousRoute !== route) {
            // revoke access of previous route
            if (previousRoute) {
                routeStore.revokeAccess(previousRoute);
                routeStore.setAccessOverlay(undefined); // hide overlay if it is still open (eg user uses back button)
            }

            // trigger access check if needed
            if (!routeStore.hasAccess(route) && route?.shouldAllowAccess) {
                routeStore.triggerAccessCheck(route, undefined, undefined);
            }

            setPreviousRoute(route);
        }
    }, [route, previousRoute, routeStore]);

    if (!routeStore.hasAccess(route)) {
        return <></>;
    }

    if (!route) {
        return <></>;
    }

    const originalRoute: IBlockedRoute = {
        route,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params,
        locationSearch: location.search,
        key
    };
    const thisRoute = useUnsavedBlockedRoute(originalRoute);

    return (
        <CurrentRouteProvider originalRoute={originalRoute}>
            <Suspense fallback={<Loading />}>
                {withParentWrapper(
                    thisRoute.route,
                    <React.Fragment key={thisRoute.key}>
                        {thisRoute.route.render?.({
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            params: thisRoute.params,
                            search: thisRoute.locationSearch,
                            loggedInUserStore,
                            refresh
                        })}
                    </React.Fragment>,
                    routeStore.flattenedRoutes
                )}
            </Suspense>
        </CurrentRouteProvider>
    );
});

const withParentWrapper = (
    route: IRoute,
    children: ReactNode,
    flattenedRoutes: IRoute[]
): ReactNode => {
    if (route.isPrintableView) {
        return children;
    }

    const parent = flattenedRoutes.find(x =>
        x.children?.find(y => y === route)
    );

    return parent
        ? withParentWrapper(
              parent,
              parent.wrapChildrenRender
                  ? parent.wrapChildrenRender(children)
                  : children,
              flattenedRoutes
          )
        : children;
};

const Loading = () => {
    return (
        <LoadingContainer>
            <Spinner />
        </LoadingContainer>
    );
};

const LoadingContainer = styled("div")({
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});
